import { definitionDecorator, EventMap, tools } from '../object-plus/index.js';
import { Transactional } from '../transactions.js';
import { getMetatype, SharedType } from './metatypes/index.js';
const { assign } = tools;
export class ChainableAttributeSpec {
    constructor(options) {
        this.options = { getHooks: [], transforms: [], changeHandlers: [] };
        if (options)
            assign(this.options, options);
    }
    check(check, error) {
        function validate(model, value, name) {
            if (!check.call(model, value, name)) {
                const msg = error || check.error || name + ' is not valid';
                return typeof msg === 'function' ? msg.call(model, name) : msg;
            }
        }
        const prev = this.options.validate;
        return this.metadata({
            validate: prev ? (function (model, value, name) {
                return prev(model, value, name) || validate(model, value, name);
            }) : validate
        });
    }
    get as() {
        return definitionDecorator('attributes', this);
    }
    get isRequired() {
        return this.required;
    }
    get required() {
        return this.metadata({ isRequired: true });
    }
    endpoint(endpoint) {
        return this.metadata({ endpoint });
    }
    watcher(ref) {
        return this.metadata({ _onChange: ref });
    }
    parse(fun) {
        return this.metadata({ parse: fun });
    }
    toJSON(fun) {
        return this.metadata({
            toJSON: typeof fun === 'function' ? fun : (fun ? (x, k, o) => x && x.toJSON(o) : emptyFunction)
        });
    }
    get(fun) {
        return this.metadata({
            getHooks: this.options.getHooks.concat(fun)
        });
    }
    set(fun) {
        function handleSetHook(next, prev, record, options) {
            if (this.isChanged(next, prev)) {
                const changed = fun.call(record, next, this.name);
                return changed === void 0 ? prev : this.convert(changed, prev, record, options);
            }
            return prev;
        }
        return this.metadata({
            transforms: this.options.transforms.concat(handleSetHook)
        });
    }
    changeEvents(events) {
        return this.metadata({ changeEvents: events });
    }
    events(map) {
        const eventMap = new EventMap(map);
        function handleEventsSubscribtion(next, prev, record) {
            prev && prev.trigger && eventMap.unsubscribe(record, prev);
            next && next.trigger && eventMap.subscribe(record, next);
        }
        return this.metadata({
            changeHandlers: this.options.changeHandlers.concat(handleEventsSubscribtion)
        });
    }
    get has() {
        return this;
    }
    metadata(options) {
        const cloned = new ChainableAttributeSpec(this.options);
        assign(cloned.options, options);
        return cloned;
    }
    value(x) {
        return this.metadata({ value: x, hasCustomDefault: true });
    }
    static from(spec) {
        if (spec && spec instanceof ChainableAttributeSpec) {
            return spec;
        }
        return typeof spec === 'function' ? type(spec) : value(spec);
    }
}
function emptyFunction() { }
export function type(Type, value) {
    if (Type instanceof ChainableAttributeSpec)
        return Type;
    const attrDef = new ChainableAttributeSpec({ type: Type }), defaultValue = Type && value === void 0 ? getMetatype(Type).defaultValue : value;
    return defaultValue === void 0 ? attrDef : attrDef.value(defaultValue);
}
export function shared(Constructor) {
    return new ChainableAttributeSpec({
        value: null,
        type: Constructor,
        _metatype: SharedType
    });
}
export function value(x) {
    const Type = inferType(x), AttrDef = Type && Type.prototype instanceof Transactional ? shared(Type) :
        type(Type);
    return AttrDef.value(x);
}
function inferType(value) {
    switch (typeof value) {
        case 'number':
            return Number;
        case 'string':
            return String;
        case 'boolean':
            return Boolean;
        case 'function':
            return Function;
        case 'undefined':
            return void 0;
        case 'object':
            return value ? value.constructor : void 0;
    }
}
//# sourceMappingURL=attrDef.js.map